/**
 * Message Rate Limiting Utility
 *
 * Prevents spam/phishing by limiting how many new conversations
 * users can initiate within a time window.
 *
 * Rules:
 * - Accounts < 7 days old AND not verified: 3/hour, 5/day limit
 * - All users: Alert support if > 3 new conversations/hour
 * - Exempt: Messages to support
 */

import { doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '@util/firebase';
import { UserDocument, MessageInitiation } from '@util/types/firestore/users';
import {
  createChat,
  getNextCaseNumber,
} from '@util/firestore/messages/messages.service';
import {
  ChatDocument,
  ChatDocumentSchema,
  ChatMessagesDocumentSchema,
} from '@util/types/firestore/chat';

// Support account ID - messages to support are exempt from rate limits
export const SUPPORT_ID = 'KAA1SDJQn1SkRXB4Mzywf28aPFx1';

// Slack webhook for 911 alerts
const SLACK_911_WEBHOOK_URL =
  'https://hooks.slack.com/services/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';

// Rate limit constants
const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;
const SEVEN_DAYS_MS = 7 * DAY_MS;
const MAX_PER_HOUR = 3;
const MAX_PER_DAY = 5;
const ALERT_THRESHOLD = 3; // Alert support when ANY user exceeds this per hour

export interface RateLimitResult {
  allowed: boolean;
  reason?: string;
  shouldAlert?: boolean;
  initiationsLastHour?: number;
}

/**
 * Checks if a user is subject to rate limiting
 * (account < 7 days old AND not verified)
 */
export function isRateLimited(userDoc: UserDocument): boolean {
  const now = Date.now();
  const accountAge = now - userDoc.created;
  const isNewAccount = accountAge < SEVEN_DAYS_MS;
  const isVerified = userDoc.permissions?.is_verified === true;

  return isNewAccount && !isVerified;
}

/**
 * Counts message initiations within a time window
 */
function countInitiationsInWindow(
  initiations: MessageInitiation[] | undefined,
  windowMs: number
): number {
  if (!initiations || initiations.length === 0) return 0;

  const cutoff = Date.now() - windowMs;
  return initiations.filter((i) => i.timestamp > cutoff).length;
}

/**
 * Gets recent recipients for alert message
 */
function getRecentRecipients(
  initiations: MessageInitiation[] | undefined,
  windowMs: number
): string[] {
  if (!initiations || initiations.length === 0) return [];

  const cutoff = Date.now() - windowMs;
  return initiations
    .filter((i) => i.timestamp > cutoff)
    .map((i) => i.recipient_id);
}

/**
 * Checks if user can initiate a new conversation
 * Returns whether allowed, reason if blocked, and whether to alert support
 */
export function checkMessageRateLimit(
  userDoc: UserDocument,
  recipientId: string
): RateLimitResult {
  // Always allow messages to support
  if (recipientId === SUPPORT_ID) {
    return { allowed: true };
  }

  // Admin and support accounts are exempt from rate limits and alerts
  if (
    userDoc.uid === SUPPORT_ID ||
    userDoc.roles?.admin === true ||
    userDoc.roles?.support === true
  ) {
    return { allowed: true };
  }

  const initiations = userDoc.message_initiations;
  const initiationsLastHour = countInitiationsInWindow(initiations, HOUR_MS);
  const initiationsLastDay = countInitiationsInWindow(initiations, DAY_MS);

  // Check if this user should trigger a support alert (any user > 3/hour)
  // We check BEFORE incrementing, so > ALERT_THRESHOLD means they've already sent 3+
  const shouldAlert = initiationsLastHour >= ALERT_THRESHOLD;

  // If user is not rate limited (verified or account > 7 days), allow but maybe alert
  if (!isRateLimited(userDoc)) {
    return {
      allowed: true,
      shouldAlert,
      initiationsLastHour: initiationsLastHour + 1, // +1 for the current message
    };
  }

  // User IS rate limited - check limits
  if (initiationsLastHour >= MAX_PER_HOUR) {
    return {
      allowed: false,
      reason: 'hourly',
      shouldAlert,
      initiationsLastHour,
    };
  }

  if (initiationsLastDay >= MAX_PER_DAY) {
    return {
      allowed: false,
      reason: 'daily',
      shouldAlert,
      initiationsLastHour,
    };
  }

  // Allowed, but check if we should alert after this message
  return {
    allowed: true,
    shouldAlert: initiationsLastHour + 1 > ALERT_THRESHOLD,
    initiationsLastHour: initiationsLastHour + 1,
  };
}

/**
 * Records a new message initiation in the user's document
 * Also cleans up old entries (older than 24 hours)
 */
export async function recordMessageInitiation(
  userId: string,
  recipientId: string
): Promise<void> {
  const userRef = doc(db, 'users', userId);

  const initiation: MessageInitiation = {
    timestamp: Date.now(),
    recipient_id: recipientId,
  };

  await updateDoc(userRef, {
    message_initiations: arrayUnion(initiation),
  });
}

/**
 * Sends a Slack alert to the 911 channel
 */
async function sendSlackAlert(
  userDoc: UserDocument,
  initiationsLastHour: number,
  recipientIds: string[]
): Promise<void> {
  const accountAgeMs = Date.now() - userDoc.created;
  const accountAgeDays = Math.floor(accountAgeMs / DAY_MS);

  const payload = {
    blocks: [
      {
        type: 'header',
        text: {
          type: 'plain_text',
          text: '⚠️ Message Rate Limit Alert',
          emoji: true,
        },
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: `*User:* <https://gearfocus.com/shop-seller/${userDoc.username_slug}|${userDoc.username}>\n*UID:* ${userDoc.uid}\n*Email:* ${userDoc.email}`,
        },
      },
      {
        type: 'section',
        fields: [
          {
            type: 'mrkdwn',
            text: `*Messages (last hour):* ${initiationsLastHour}`,
          },
          {
            type: 'mrkdwn',
            text: `*Account age:* ${accountAgeDays} days`,
          },
          {
            type: 'mrkdwn',
            text: `*Verified:* ${userDoc.permissions?.is_verified ? 'Yes' : 'No'}`,
          },
          {
            type: 'mrkdwn',
            text: `*Is Seller:* ${userDoc.account_activated ? 'Yes' : 'No'}`,
          },
        ],
      },
      {
        type: 'section',
        text: {
          type: 'mrkdwn',
          text: `*Recent recipients (${recipientIds.length}):*\n${recipientIds.slice(0, 10).join(', ')}${recipientIds.length > 10 ? '...' : ''}`,
        },
      },
      {
        type: 'context',
        elements: [
          {
            type: 'mrkdwn',
            text: `Alert triggered at ${new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' })} CT`,
          },
        ],
      },
    ],
  };

  try {
    await fetch(SLACK_911_WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    console.error('Failed to send Slack alert:', error);
  }
}

/**
 * Creates a support message alerting staff about potential spam
 */
async function createSupportAlertMessage(
  userDoc: UserDocument,
  initiationsLastHour: number,
  recipientIds: string[]
): Promise<void> {
  const accountAgeMs = Date.now() - userDoc.created;
  const accountAgeDays = Math.floor(accountAgeMs / DAY_MS);
  const now = Date.now();

  const alertContent = `⚠️ Rate Limit Alert

User "${userDoc.username}" has initiated ${initiationsLastHour} new conversations in the past hour.

Account Details:
• UID: ${userDoc.uid}
• Email: ${userDoc.email}
• Account age: ${accountAgeDays} days
• Verified: ${userDoc.permissions?.is_verified ? 'Yes' : 'No'}
• Is Seller: ${userDoc.account_activated ? 'Yes' : 'No'}

Recent message recipients (${recipientIds.length}):
${recipientIds.slice(0, 10).map((id) => `• ${id}`).join('\n')}${recipientIds.length > 10 ? '\n• ...' : ''}

Profile: https://gearfocus.com/shop-seller/${userDoc.username_slug}

Please review for potential spam/phishing activity.`;

  const message = ChatMessagesDocumentSchema.parse({
    uid: 'GearBot',
    content: alertContent,
    is_img: false,
    created_at: now,
  });

  const caseNum = await getNextCaseNumber();

  const chat: ChatDocument = ChatDocumentSchema.parse({
    uids: [userDoc.uid, SUPPORT_ID],
    buyer_id: userDoc.uid,
    seller_id: SUPPORT_ID,
    messages: [message],
    case_num: caseNum,
    is_bot: true,
    created_at: now,
    unread: {
      [SUPPORT_ID]: true,
      [userDoc.uid]: false,
    },
  });

  await createChat(chat);
}

/**
 * Sends alerts to support via Slack and creates a support message
 */
export async function sendRateLimitAlerts(
  userDoc: UserDocument,
  initiationsLastHour: number
): Promise<void> {
  const recipientIds = getRecentRecipients(
    userDoc.message_initiations,
    HOUR_MS
  );

  // Send both alerts in parallel
  await Promise.all([
    sendSlackAlert(userDoc, initiationsLastHour, recipientIds),
    createSupportAlertMessage(userDoc, initiationsLastHour, recipientIds),
  ]);
}

/**
 * Gets a user-friendly message explaining why they're rate limited
 */
export function getRateLimitMessage(reason: 'hourly' | 'daily'): {
  title: string;
  message: string;
} {
  if (reason === 'hourly') {
    return {
      title: 'Message limit reached',
      message: `To help keep our community safe, new accounts can send up to ${MAX_PER_HOUR} messages per hour. Your limit will reset shortly.\n\nWant to message more people?\n• Become a verified seller to remove limits\n• Contact support if you have a legitimate need`,
    };
  }

  return {
    title: 'Daily message limit reached',
    message: `To help keep our community safe, new accounts can send up to ${MAX_PER_DAY} messages per day. Your limit will reset tomorrow.\n\nWant to message more people?\n• Become a verified seller to remove limits\n• Contact support if you have a legitimate need`,
  };
}
